import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { Property, PropertySchema, PropertyStatus } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { sendSearchListRequests } from "~/classes/handlers/search/list"
import { InterfaceSearch } from "~/classes/search/InterfaceSearch"

class Search extends GlobalComposable {
  listSearch = (searchId: string, pageSize: number, isOffline: boolean) => {
    sendSearchListRequests([{
      requestId: generateWorkerRequestId(),
      offset: 0,
      search: InterfaceSearch.blank(searchId).inner(),
      pageSize,
      limit: pageSize,
      offline: isOffline,
      /**
       * TODO: migrate adding file properties in search, which allows you to have
       * file properties associated with the search item
       */
      fileSelection: InterfaceSelection.minimumInterfaceSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
    }])
  }

  /**
   * Creates a search and returns a struct of the request and a promise of the result. The
   * search times out after either the requested number of MS or the default timeout.
   * NOTE: the search response doesn't contain any results, for that you have to keep waiting
   * for the search list response(s).
   */
  createSearch = async (
    { locations, definition, pageSize, limit, offline }: {
      locations: string[]
      definition: SearchDefinition
      pageSize: number
      limit: number
      offline: boolean
    },
  ) => {
    const { state: myself } = useSelf()

    if (!isDefined(myself.value.user))
      throw new Error("Must be logged in to search currently")

    const preHash = InterfaceSearch.clientPreHashId()
    const searchId = await InterfaceSearch.idFromPrehash(preHash)
    const timestamp = uuidv7()
    const search = InterfaceSearch.blank(searchId)
    search.addProperty({
      propertyId: Property.SEARCH_LOCATIONS,
      searchId,
      schema: PropertySchema.SearchLocationsValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.SearchLocationsValue, locations },
    })
    search.addProperty({
      propertyId: Property.SEARCH_USER,
      searchId,
      schema: PropertySchema.UserIdValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.UserIdValue, userId: myself.value.user.userId },
    })
    search.addProperty({
      propertyId: Property.SEARCH_CREATED_AT,
      searchId,
      schema: PropertySchema.UtcTimestampMillisValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: InterfaceFile.asTimestampMillis(timestamp) },
    })
    search.addProperty({
      propertyId: Property.SEARCH_EXECUTED_AT,
      searchId,
      schema: PropertySchema.UtcTimestampMillisValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: InterfaceFile.asTimestampMillis(timestamp) },
    })
    search.addProperty({
      propertyId: Property.SEARCH_DEFINITION,
      searchId,
      schema: PropertySchema.SearchDefinitionValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.SearchDefinitionValue, definition },
    })

    const requestId = generateWorkerRequestId()
    const request: SearchListRequestDatagram = {
      requestId,
      offline, // TODO: offline searches
      preHash,
      pageSize,
      limit,
      offset: 0,
      search: search.inner(),
      fileSelection: InterfaceSelection.minimumInterfaceSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
    }
    sendSearchListRequests([request])

    return request
  }
}

const search = new Search("search", theme.colors.pink.hex)
export default function () {
  return search.use()
}
